import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Camera, ChevronRight, Download, Film, Folder, FolderOpen, HardDrive, Image as ImageIcon, Play, Search, Store, Trash2, UserRound, Video, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Pagination, { paginate } from '../../components/Pagination';
import API from '../../utils/api';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import '../IncompleteRegistrations/IncompleteRegistrations.css';
import './CallRecordings.css';

const PAGE_SIZE = 10;
const origin = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const bytes = (n) => (n >= 1073741824 ? `${(n / 1073741824).toFixed(1)} GB` : n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const when = (d) => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const dur = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.round(s % 60)).padStart(2, '0')}`;

/**
 * Recordings and screenshots made during video calls, kept in a folder for each member / service provider.
 * First the folders (one per person), then the files inside the one you open.
 */
const CallRecordings = () => {
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(null); // the folder being viewed
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [kind, setKind] = useState('all');
    const [page, setPage] = useState(1);
    const [preview, setPreview] = useState(null);
    const [confirm, confirmDialog] = useConfirmDialog();

    const loadFolders = useCallback(async () => {
        try {
            const res = await API.get('/calls/media/folders');
            setFolders(res.data.folders || []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not load the folders');
        } finally { setLoading(false); }
    }, []);

    const loadFiles = useCallback(async (folder) => {
        setFilesLoading(true);
        try {
            const res = await API.get('/calls/media', { params: { target: folder.userId } });
            setFiles(res.data.files || []);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not load the files');
        } finally { setFilesLoading(false); }
    }, []);

    useEffect(() => { loadFolders(); }, [loadFolders]);

    const openFolder = (folder) => { setOpen(folder); setKind('all'); setPage(1); setFiles([]); loadFiles(folder); };
    const closeFolder = () => { setOpen(null); setFiles([]); loadFolders(); };

    const shownFolders = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return needle ? folders.filter(f => [f.name, f.code].filter(Boolean).some(v => String(v).toLowerCase().includes(needle))) : folders;
    }, [folders, query]);
    const shownFiles = useMemo(() => files.filter(f => kind === 'all' || f.kind === kind), [files, kind]);
    const totalSize = folders.reduce((n, f) => n + f.size, 0);

    const remove = async (file) => {
        if (!(await confirm({
            title: `Delete ${file.kind === 'recording' ? 'this recording' : 'this screenshot'}?`,
            message: `${file.fileName} will be deleted from the server. This cannot be undone.`,
            confirmLabel: 'Delete', danger: true, icon: Trash2,
        }))) return;
        try {
            await API.delete(`/calls/media/${file._id}`);
            setFiles(current => current.filter(f => f._id !== file._id));
            if (preview && preview._id === file._id) setPreview(null);
            toast.success('Deleted');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not delete the file');
        }
    };

    const url = (file, download) => `${origin}${file.url}${download ? '&download=1' : ''}`;

    return (
        <div className="incomplete-layout">
            <Sidebar />
            <main className="admin-content incomplete-content">
                <Navbar title={<span className="incomplete-title"><Video size={19} /> Call Recordings</span>} />
                <div className="incomplete-page">
                    <header className="incomplete-heading">
                        <div>
                            <h2>Call recordings</h2>
                            <p>Recordings and screenshots from video calls. Each member and service provider has a folder of their own. Only admins can open them.</p>
                        </div>
                        <span className="incomplete-total"><HardDrive size={16} /> {folders.length} {folders.length === 1 ? 'folder' : 'folders'} · {bytes(totalSize)}</span>
                    </header>

                    <section className="incomplete-panel">
                        {!open ? (
                            <>
                                <div className="incomplete-toolbar">
                                    <label className="incomplete-search"><Search size={17} /><input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a name or ID..." /></label>
                                </div>
                                {loading ? <div className="incomplete-state">Loading folders...</div> : shownFolders.length === 0 ? (
                                    <div className="incomplete-state"><Folder size={38} /><strong>{folders.length ? 'No folder found' : 'No recordings yet'}</strong><span>{folders.length ? 'Try a different name or ID.' : 'Start a video call from the Users page, then use Record or Screenshot during the call.'}</span></div>
                                ) : (
                                    <div className="cr-folders">
                                        {shownFolders.map(f => (
                                            <button type="button" key={f.userId} className="cr-folder" onClick={() => openFolder(f)}>
                                                <span className="cr-folder__icon"><Folder size={34} /></span>
                                                <span className="cr-folder__body">
                                                    <strong>{f.name || 'Unnamed'}</strong>
                                                    <small>{f.code || '—'}</small>
                                                    <span className={`cr-role is-${f.role}`}>{f.role === 'service' ? <><Store size={11} /> Service provider</> : <><UserRound size={11} /> Member</>}</span>
                                                </span>
                                                <span className="cr-folder__meta">
                                                    <span><Film size={13} /> {f.recordings} {f.recordings === 1 ? 'recording' : 'recordings'}</span>
                                                    <span><Camera size={13} /> {f.screenshots} {f.screenshots === 1 ? 'screenshot' : 'screenshots'}</span>
                                                    <small>{bytes(f.size)} · {when(f.lastAt)}</small>
                                                </span>
                                                <ChevronRight size={18} className="cr-folder__go" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="cr-crumbs">
                                    <button type="button" onClick={closeFolder}><ArrowLeft size={15} /> All folders</button>
                                    <ChevronRight size={14} />
                                    <span><FolderOpen size={15} /> {open.name} <small>{open.code}</small></span>
                                </div>
                                <div className="incomplete-tabs cr-tabs">
                                    {[['all', 'All', files.length], ['recording', 'Recordings', files.filter(f => f.kind === 'recording').length], ['screenshot', 'Screenshots', files.filter(f => f.kind === 'screenshot').length]].map(([value, label, count]) => (
                                        <button type="button" key={value} className={kind === value ? 'active' : ''} onClick={() => { setKind(value); setPage(1); }}>{label}<span>{count}</span></button>
                                    ))}
                                </div>
                                {filesLoading ? <div className="incomplete-state">Loading files...</div> : shownFiles.length === 0 ? (
                                    <div className="incomplete-state"><ImageIcon size={34} /><strong>Nothing here</strong><span>No files of this kind in this folder.</span></div>
                                ) : (
                                    <>
                                        <div className="cr-files">
                                            {paginate(shownFiles, page, PAGE_SIZE).map(file => (
                                                <div className="cr-file" key={file._id}>
                                                    <button type="button" className="cr-file__thumb" onClick={() => setPreview(file)} aria-label={`Open ${file.fileName}`}>
                                                        {file.kind === 'screenshot'
                                                            ? <img src={url(file)} alt="" loading="lazy" />
                                                            : <video src={`${url(file)}#t=0.5`} preload="metadata" muted />}
                                                        {file.kind === 'recording' && <span className="cr-file__play"><Play size={20} fill="currentColor" /></span>}
                                                        <span className={`cr-file__kind is-${file.kind}`}>{file.kind === 'recording' ? <><Film size={11} /> {dur(file.durationSec || 0)}</> : <><Camera size={11} /> Photo</>}</span>
                                                    </button>
                                                    <div className="cr-file__info">
                                                        <strong title={file.fileName}>{file.fileName}</strong>
                                                        <small>{when(file.createdAt)} · {bytes(file.size)}</small>
                                                    </div>
                                                    <div className="cr-file__actions">
                                                        <button type="button" onClick={() => setPreview(file)} title="Open"><Play size={15} /></button>
                                                        <a href={url(file, true)} title="Download" download><Download size={15} /></a>
                                                        <button type="button" className="is-danger" onClick={() => remove(file)} title="Delete"><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <Pagination page={page} total={shownFiles.length} pageSize={PAGE_SIZE} onChange={setPage} />
                                    </>
                                )}
                            </>
                        )}
                    </section>
                </div>
            </main>

            {confirmDialog}
            {preview && (
                <div className="cr-modal" role="presentation" onClick={() => setPreview(null)}>
                    <div className="cr-modal__card" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                        <header>
                            <div><strong>{preview.fileName}</strong><small>{when(preview.createdAt)} · {bytes(preview.size)}{preview.kind === 'recording' ? ` · ${dur(preview.durationSec || 0)}` : ''}</small></div>
                            <button type="button" onClick={() => setPreview(null)} aria-label="Close"><X size={18} /></button>
                        </header>
                        <div className="cr-modal__media">
                            {preview.kind === 'recording' ? <video src={url(preview)} controls autoPlay playsInline /> : <img src={url(preview)} alt={preview.fileName} />}
                        </div>
                        <footer>
                            <a href={url(preview, true)} download className="is-main"><Download size={15} /> Download</a>
                            <button type="button" className="is-danger" onClick={() => remove(preview)}><Trash2 size={15} /> Delete</button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallRecordings;
